import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import isString from "lodash/isString";
import isEqual from "lodash/isEqual";
import ChecklistForm from "./ChecklistForm";
import {
    addItem,
    clearState,
    editChecklistAction,
    editChecklistItem,
    addChecklistItem,
    reorderItems,
    removeChecklistAction,
    removeChecklistItem,
    removeItem,
    saveItem,
    saveLabel,
    saveDescription,
    setCurrentChecklist,
    saveChecklist
} from "./redux/actions";
import fetchChecklistsAction from "./redux/actions/fetchChecklistsAction";
import { requireAtleastOneItem, requireMinimumLength } from "./helpers";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const EditChecklist = (props) => {

    const [error, setError] = useState("");
    const history = useHistory();

    const handleSubmit = async () => {
        const {
            initialChecklist,
            checklist,
            t,
            setCurrentChecklist,
            setActive,
            editChecklist,
            saveChecklist,
            applyChecklist,
            addChecklistItem,
            editChecklistItem,
            removeChecklistItem
        } = props;

        // nothing changed, go to checklist list
        if (isEqual(initialChecklist, checklist)) {
            return;
        }

        const { label } = checklist;

        const strLabel = isString(label) ? label.trim() : "";

        // label changed validate length
        if (initialChecklist.label !== strLabel) {
            if (!requireMinimumLength(strLabel)) {
                setError(t("okapi.error.label.length"));
                return;
            }
        }

        let errors = false;

        if (!isEqual(checklist.items, initialChecklist.items)) {

            const { items } = checklist;
            const newItems = [...items];
            const lastItem = newItems.pop();

            if (!requireAtleastOneItem(items)) {
                setError(t("A checklist needs atleast one item"));
                return;
            }

            const mappedItems = newItems.map(item => {
                const { label } = item;
                if (!requireMinimumLength(label)) {
                    errors = true;
                    return { ...item, error: t("okapi.error.label.length") };
                }
                return item;
            });

            if (errors) {
                setCurrentChecklist(
                    {
                        label: strLabel, description: checklist.description, items: [...mappedItems, lastItem]
                    });
            }
        }

        if (errors) {
            return;
        }

        const { items } = checklist;
        const newItems = [...items];
        newItems.pop();
        await editChecklist();

        saveChecklist({ ...checklist, items: newItems });
        applyChecklist({ ...checklist, items: items });
        setActive(false); 
    };

    
    const handleDelete = async () => {
        const {
            checklist,
            removeChecklist,
            fetchChecklists,
            organisationId,
            t,
            applyChecklist,
            index,
            setActive
        } = props;

        await removeChecklist(checklist.id);
        await fetchChecklists(organisationId, t);
        applyChecklist(null, index);
        setActive(false);
    };

    const {
        index,
        t,
        checklist,
        organisationId,
        setActive,
        applyChecklist,
        addItem,
        saveItem,
        removeItem,
        saveLabel,
        saveDescription,
        reorderItems,
        initialChecklist,
        readOnly,
    } = props;

    return (
        <div className="card checklist-list">
            <div className="card-header">
                <div className="card-header-title">
                    {readOnly
                        ? t("checklist.preview")
                        : t("common.edit.what", { what: t("checklist.singular") })}
                </div>
                <span className="card-header-icon is-pulled-right"
                    onClick={() => setActive(false)}>
                    <span className="icon">
                        <i className="fas fa-times fa-lg" />
                    </span>
                </span>
            </div>
            <div className="card-content">
                <div className="content">
                    <ChecklistForm
                        index={index}
                        addItem={addItem}
                        saveItem={saveItem}
                        removeItem={removeItem}
                        checklist={checklist}
                        error={error}
                        saveLabel={saveLabel}
                        saveDescription={saveDescription}
                        reorderItems={reorderItems}
                        readOnly={readOnly}
                    />
                </div>
            </div>
            {!readOnly ? (
                <div className="card-footer">
                    <div className="card-footer-item">
                        <div style={{ width: "100%" }}>
                            <div className="button is-success"
                                style={{ width: "100%" }}
                                onClick={() => handleSubmit()}
                                data-testid="editChecklistSave">
                                {t("button.edit.label")}
                            </div>
                            <div className="button is-danger"
                                style={{ width: "100%", marginTop: "0.5rem" }}
                                onClick={() => handleDelete()}
                                data-testid="editChecklistDelete">
                                {t("button.delete.label")}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="card-footer">
                    <div className="card-footer-item">
                        <button
                            className="button is-success is-fullwidth"
                            type="button"
                            onClick={() => history.push("/measures")}
                            data-testid="editChecklistGoToMeasure"
                        >
                            {t("task.checklist.button.openMeasure")}
                        </button>
                    </div>
                </div>
            )}
        </div>);
};

EditChecklist.propTypes = {
    index: PropTypes.number.isRequired,
    addItem: PropTypes.func.isRequired,
    checklist: PropTypes.object.isRequired,
    initialChecklist: PropTypes.object.isRequired,
    organisationId: PropTypes.string.isRequired,
    removeItem: PropTypes.func.isRequired,
    saveItem: PropTypes.func.isRequired,
    saveLabel: PropTypes.func.isRequired,
    saveDescription: PropTypes.func.isRequired,
    setActive: PropTypes.func.isRequired,
    applyChecklist: PropTypes.func.isRequired,
    setCurrentChecklist: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    editChecklist: PropTypes.func.isRequired,
    editChecklistItem: PropTypes.func.isRequired,
    addChecklistItem: PropTypes.func.isRequired,
    removeChecklist: PropTypes.func.isRequired,
    removeChecklistItem: PropTypes.func.isRequired,
    saveChecklist: PropTypes.func.isRequired,
    reorderItems: PropTypes.func.isRequired,
    fetchChecklists: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

const mapStateToProps = (state) => {
    const { manageChecklists } = state;
    const { checklist, initialChecklist } = manageChecklists;
    return { checklist, initialChecklist };
};

const mapDispatchToProps = (dispatch) => {
    return {
        addItem: () => {
            dispatch(addItem());
        },
        saveItem: (itemIndex, item) => {
            dispatch(saveItem(itemIndex, item));
        },
        removeItem: (itemIndex) => {
            dispatch(removeItem(itemIndex));
        },
        saveLabel: (label) => {
            dispatch(saveLabel(label));
        },
        saveDescription: (description) => {
            dispatch(saveDescription(description));
        },
        setCurrentChecklist: (checklist) => {
            dispatch(setCurrentChecklist(checklist));
        },
        clearState: () => {
            dispatch(clearState());
        },
        saveChecklist: (checklist) => {
            dispatch(saveChecklist(checklist));
        },
        editChecklist: () => {
            return dispatch(editChecklistAction());
        },
        removeChecklist: (checklistId) => {
            return dispatch(removeChecklistAction(checklistId));
        },
        fetchChecklists: (organisationId, t) => {
            return dispatch(fetchChecklistsAction(organisationId, t));
        },
        removeChecklistItem: (checklistId, item) => {
            return dispatch(removeChecklistItem(checklistId, item));
        },
        editChecklistItem: (checklistId, item) => {
            return dispatch(editChecklistItem(checklistId, item));
        },
        addChecklistItem: (checklistId, item) => {
            return dispatch(addChecklistItem(checklistId, item));
        },
        reorderItems: (fromIndex, toIndex) => {
            dispatch(reorderItems(fromIndex, toIndex));
        }
    };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(EditChecklist));
